import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "./auth";
import type { TranscriptionListItem } from "./lib/api";
import { localizedError } from "./lib/localized-error";

interface TaskContextValue {
  tasks: TranscriptionListItem[];
  loading: boolean;
  error: string | null;
  refresh: (query?: string) => Promise<void>;
  prepend: (task: TranscriptionListItem) => void;
}

const TaskContext = createContext<TaskContextValue | null>(null);

export function TaskProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { api, session } = useAuth();
  const [tasks, setTasks] = useState<TranscriptionListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const refresh = useCallback(async (query = "") => {
    if (!session) {
      setTasks([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setTasks(await api.listTranscriptions(query));
    } catch (reason) {
      setError(localizedError(reason, t));
    } finally {
      setLoading(false);
    }
  }, [api, session, t]);
  useEffect(() => { void refresh(); }, [refresh]);
  useEffect(() => {
    if (!session) return;
    const timer = window.setInterval(() => void refresh(), 10_000);
    return () => window.clearInterval(timer);
  }, [refresh, session]);
  const value = useMemo<TaskContextValue>(() => ({
    tasks,
    loading,
    error,
    refresh,
    prepend: (task) => setTasks((current) => [task, ...current.filter((item) => item.id !== task.id)]),
  }), [error, loading, refresh, tasks]);
  return <TaskContext.Provider value={value}>{children}</TaskContext.Provider>;
}

export function useTasks(): TaskContextValue {
  const value = useContext(TaskContext);
  if (!value) throw new Error("useTasks must be used inside TaskProvider");
  return value;
}
